import { Component, Prop, State, Element, Watch, Method, Event, EventEmitter, Host, h } from '@stencil/core';
import { AuthService } from '../../services/auth.service';
import { ApiService } from '../../services/api.service';
import { ThemeService } from '../../services/theme.service';
import { WidgetState, WidgetEvent, ChatMessage } from '../../types/api';

@Component({
  tag: 'bettercx-widget',
  styleUrl: 'bettercx-widget.scss',
  shadow: true,
})
export class BetterCXWidget {
  @Element() el: HTMLElement;

  // Simplified public properties - only essential ones
  @Prop() publicKey: string;
  @Prop() theme: 'light' | 'dark' | 'auto' = 'auto';
  @Prop() debug: boolean = false;
  @Prop() baseUrl: string = 'https://dev-api.bettercx.ai';
  @Prop() aiServiceUrl: string = 'https://dev-ai.bettercx.ai';
  @Prop() autoInit: boolean = true;
  @Prop() position: 'left' | 'right' = 'right';

  // Internal state
  @State() state: WidgetState = {
    isOpen: false,
    isAuthenticated: false,
    isLoading: true,
    messages: [],
    isTyping: false,
  };

  // Language state
  @State() language: 'pl' | 'en' = 'en';

  // Services
  private authService: AuthService;
  private apiService: ApiService;
  private themeService: ThemeService;

  // Refs
  private messagesContainerRef: HTMLDivElement;

  // Events
  @Event() widgetEvent: EventEmitter<WidgetEvent>;

  @Watch('publicKey')
  async onPublicKeyChange() {
    if (this.publicKey) {
      await this.initialize();
    }
  }

  async componentWillLoad() {
    if (this.publicKey && this.autoInit) {
      await this.initialize();
    }
  }

  async componentDidLoad() {
    if (this.themeService) {
      this.themeService.watchWebsiteTheme(() => {
        this.themeService.setDefaultTheme();
      });
    }
  }

  async initialize() {
    if (!this.publicKey) {
      this.setState({ isLoading: false, isAuthenticated: false });
      return;
    }

    try {
      this.setState({ isLoading: true, error: undefined });

      this.authService = new AuthService(this.baseUrl);
      const origin = window.location.origin;
      const sessionData = await this.authService.createSession(this.publicKey, origin);

      this.apiService = new ApiService(this.baseUrl, this.aiServiceUrl, this.authService);
      this.themeService = new ThemeService(this.el);

      this.language = await this.themeService.detectWebsiteLanguage();
      this.themeService.setDefaultTheme();

      if ('attrs' in sessionData && sessionData.attrs) {
        this.applyCustomColors(sessionData.attrs as { dark_mode?: Record<string, unknown>; light_mode?: Record<string, unknown> });
        this.applyColorsToMessageComposer();
      }

      this.setState({
        isAuthenticated: true,
        exampleQuestions: ('example_questions' in sessionData ? sessionData.example_questions : []) as Array<{
          id: number;
          question_text: string;
          answer_text: string;
          created_at: number;
        }>,
      });
      this.emitEvent('session-created', { origin });

      this.setState({ isLoading: false });
      this.setState({ isOpen: false });
    } catch (error) {
      this.setState({
        isLoading: false,
        isAuthenticated: false,
      });
      this.emitEvent('error', { error: error.message });
    }
  }

  @Method()
  async open() {
    if (this.state.isAuthenticated) {
      this.setState({ isOpen: true });
      this.emitEvent('opened');

      this.applyColorsToMessageComposer();

      setTimeout(() => {
        this.scrollToBottom(false);
      }, 100);
    }
  }

  @Method()
  async close() {
    this.setState({ isOpen: false });
    this.emitEvent('closed');
  }

  @Method()
  async toggle() {
    if (this.state.isOpen) {
      await this.close();
    } else {
      await this.open();
    }
  }

  @Method()
  async sendMessage(content: string) {
    if (!this.state.isAuthenticated || !content.trim()) {
      return;
    }

    const userMessage: ChatMessage = {
      content: content.trim(),
      author: 'user',
      timestamp: new Date().toISOString(),
      id: this.generateId(),
    };

    this.setState({
      messages: [...this.state.messages, userMessage],
      isTyping: true,
    });

    setTimeout(() => {
      this.scrollToBottom(true);
    }, 50);

    this.emitEvent('message-sent', userMessage as unknown as Record<string, unknown>);

    try {
      const stream = await this.apiService.sendMessage(content);
      if (stream) {
        let assistantMessage: ChatMessage | null = null;
        let isStreamingStarted = false;

        const streamParser = await this.apiService.parseStreamResponse(stream);

        for await (const chunk of streamParser) {
          if (chunk.type === 'streaming_output') {
            if (!isStreamingStarted) {
              assistantMessage = {
                content: '',
                author: 'assistant',
                timestamp: new Date().toISOString(),
                id: this.generateId(),
              };

              this.setState({
                messages: [...this.state.messages, assistantMessage],
                isTyping: false,
              });
              isStreamingStarted = true;
            }

            if (assistantMessage) {
              assistantMessage.content += chunk.content;
              this.setState({
                messages: [...this.state.messages.slice(0, -1), { ...assistantMessage }],
              });
            }
          } else {
            if (!isStreamingStarted) {
              this.setState({ isTyping: true });
            }
          }
        }

        if (assistantMessage) {
          this.emitEvent('message-received', assistantMessage as unknown as Record<string, unknown>);
        }
      }
    } catch (error) {
      this.setState({ error: 'Failed to send message' });
      this.emitEvent('error', { error: error.message });
    } finally {
      this.setState({ isTyping: false });
    }
  }

  private setState(updates: Partial<WidgetState>) {
    const previousMessages = this.state.messages;
    this.state = { ...this.state, ...updates };

    if (updates.messages && updates.messages !== previousMessages) {
      setTimeout(() => {
        this.scrollToBottom(true);
      }, 0);
    }
  }

  private emitEvent(type: WidgetEvent['type'], data?: Record<string, unknown>) {
    const event: WidgetEvent = {
      type,
      data,
      timestamp: new Date().toISOString(),
    };
    this.widgetEvent.emit(event);
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  private getTranslation(key: string): string {
    const translations = {
      common_questions: {
        en: 'Common questions',
        pl: 'Często zadawane pytania',
      },
      message_placeholder: {
        en: 'Type your message...',
        pl: 'Wpisz swoją wiadomość...',
      },
    };

    return translations[key]?.[this.language] || translations[key]?.['en'] || key;
  }

  private scrollToBottom(smooth: boolean = true) {
    if (this.messagesContainerRef) {
      this.messagesContainerRef.scrollTo({
        top: this.messagesContainerRef.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }

  private handleToggleClick = () => {
    if (this.state.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  private handleMessageSubmit = (event: CustomEvent<string>) => {
    this.sendMessage(event.detail);
  };

  private applyCustomColors(attrs: { dark_mode?: Record<string, unknown>; light_mode?: Record<string, unknown> }) {
    const currentTheme = this.themeService.getCurrentTheme();
    const colorMode = currentTheme === 'dark' ? attrs.dark_mode : attrs.light_mode;

    if (colorMode && typeof colorMode === 'object') {
      this.el.style.setProperty('--bcx-primary', String(colorMode.primary_color || ''));
      this.el.style.setProperty('--bcx-secondary', String(colorMode.secondary_color || ''));
      this.el.style.setProperty('--bcx-background', String(colorMode.background_color || ''));
      this.el.style.setProperty('--bcx-text', String(colorMode.text_color || ''));

      document.documentElement.style.setProperty('--bcx-primary', String(colorMode.primary_color || ''));
      document.documentElement.style.setProperty('--bcx-secondary', String(colorMode.secondary_color || ''));
      document.documentElement.style.setProperty('--bcx-background', String(colorMode.background_color || ''));
      document.documentElement.style.setProperty('--bcx-text', String(colorMode.text_color || ''));
    }
  }

  private applyColorsToMessageComposer() {
    setTimeout(() => {
      const messageComposer = this.el.querySelector('bcx-message-composer');
      if (messageComposer) {
        const primaryColor = this.el.style.getPropertyValue('--bcx-primary') || '#007bff';
        const secondaryColor = this.el.style.getPropertyValue('--bcx-secondary') || '#6c757d';
        const backgroundColor = this.el.style.getPropertyValue('--bcx-background') || '#ffffff';
        const textColor = this.el.style.getPropertyValue('--bcx-text') || '#212529';

        messageComposer.style.setProperty('--bcx-primary', primaryColor);
        messageComposer.style.setProperty('--bcx-secondary', secondaryColor);
        messageComposer.style.setProperty('--bcx-background', backgroundColor);
        messageComposer.style.setProperty('--bcx-text', textColor);
      }
    }, 100);
  }

  private handleExampleQuestionClick = (question: { id: number; question_text: string; answer_text: string; created_at: number }) => {
    const userMessage: ChatMessage = {
      content: question.question_text,
      author: 'user',
      timestamp: new Date().toISOString(),
      id: this.generateId(),
    };

    this.setState({
      messages: [...this.state.messages, userMessage],
    });

    this.simulateTypingResponse(question.answer_text);
  };

  private simulateTypingResponse(fullText: string) {
    const assistantMessage: ChatMessage = {
      content: '',
      author: 'assistant',
      timestamp: new Date().toISOString(),
      id: this.generateId(),
    };

    this.setState({
      messages: [...this.state.messages, assistantMessage],
      isTyping: false,
    });

    let currentText = '';
    let index = 0;
    const typingSpeed = 25;

    const typeNextCharacter = () => {
      if (index < fullText.length) {
        currentText += fullText[index];
        index++;

        const updatedMessage = { ...assistantMessage, content: currentText };
        const updatedMessages = [...this.state.messages];
        updatedMessages[updatedMessages.length - 1] = updatedMessage;

        this.setState({
          messages: updatedMessages,
        });

        setTimeout(typeNextCharacter, typingSpeed);
      } else {
        this.emitEvent('message-received', assistantMessage as unknown as Record<string, unknown>);
      }
    };

    setTimeout(typeNextCharacter, 200);
  }

  render() {
    if (!this.state.isAuthenticated) {
      return null;
    }

    if (this.state.isLoading) {
      return (
        <Host class="bcx-widget bcx-widget--loading">
          <div class="bcx-widget__loading">
            <div class="bcx-widget__spinner"></div>
            <span>Loading...</span>
          </div>
        </Host>
      );
    }

    return (
      <Host class={`bcx-widget ${this.state.isOpen ? 'bcx-widget--open' : ''} bcx-widget--${this.position}`}>
        {/* Toggle Button */}
        <button class="bcx-widget__toggle" onClick={this.handleToggleClick} aria-label={this.state.isOpen ? 'Close chat' : 'Open chat'}>
          <span class="bcx-widget__toggle-icon">
            {this.state.isOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            )}
          </span>
        </button>

        {/* Chat Interface */}
        {this.state.isOpen && (
          <div class="bcx-widget__chat">
            <div class="bcx-widget__header">
              <h3>Chat AI</h3>
              <button class="bcx-widget__close" onClick={() => this.close()} aria-label="Close chat">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div class="bcx-widget__messages" ref={el => (this.messagesContainerRef = el)}>
              {this.state.messages.map(message => (
                <div key={message.id} class={`bcx-widget__message bcx-widget__message--${message.author}`}>
                  <div class="bcx-widget__message-content">{message.content}</div>
                  <div class="bcx-widget__message-time">{new Date(message.timestamp).toLocaleTimeString()}</div>
                </div>
              ))}

              {/* Example Questions - only show when no messages and questions available */}
              {this.state.messages.length === 0 && this.state.exampleQuestions && this.state.exampleQuestions.length > 0 && (
                <div class="bcx-widget__example-questions">
                  <div class="bcx-widget__example-questions-title">{this.getTranslation('common_questions')}</div>
                  {this.state.exampleQuestions.slice(0, 3).map(question => (
                    <button key={question.id} class="bcx-widget__example-question" onClick={() => this.handleExampleQuestionClick(question)}>
                      {question.question_text}
                    </button>
                  ))}
                </div>
              )}

              {this.state.isTyping && (
                <div class="bcx-widget__typing">
                  <div class="bcx-widget__typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}
            </div>

            <div class="bcx-widget__composer">
              <bcx-message-composer
                onMessageSubmit={this.handleMessageSubmit}
                disabled={this.state.isTyping}
                loading={this.state.isTyping}
                placeholder={this.getTranslation('message_placeholder')}
              />
            </div>
          </div>
        )}
      </Host>
    );
  }
}
